import { Button } from '@thanx/uikit/button'
import { Text } from '@thanx/uikit/text'
import { getMerchant } from 'actions/merchant'
import { useGetMakeGoodOffersQuery } from 'api/makeGoodLadder'
import { useNotifyActivationAdminMutation } from 'api/recoveryAi'
import type { BrandVoiceMode, PhilosophyPosture } from 'api/recoveryAi.types'
import palette from 'constants/palette'
import useCurrentMerchantUser from 'hooks/useCurrentMerchantUser'
import { useLiveUpdatesState } from 'hooks/useLiveUpdatesState'
import { buildTranslate } from 'locales'
import React, { useCallback, useMemo, useState } from 'react'
import { useDispatch, useSelector } from 'react-redux'
import { Redirect } from 'react-router-dom'
import { push } from 'react-router-redux'
import { selectCurrentMerchant } from 'selectors/merchant'
import { useStyletron } from 'styletron-react'
import { useAbility } from 'utilities/ability'
import {
  ACTIVATION_ROW_KEYS,
  ACTIVATION_ROW_MODAL_WIDTH,
  RowKey,
} from '../../components/configRowKeys'
import RecoveryConfigModals from '../../components/RecoveryConfigModals'
import StatusPill from '../../components/StatusPill'
import SectionShell from '../../PromoLanding/SectionShell'
// master's ACTIVE_LOCATIONS_SETTINGS_PATH / SETTINGS_PATH / buildConfigurePath imports
// are deliberately NOT carried over: they fed DESTINATIONS and
// PRE_ACTIVATION_DESTINATIONS, and D6 replaced every card's route hop with an
// in-place modal, so their only consumers are gone.
import {
  AGENT_TUNING_PATH,
  RECOVERY_AI_ROOT,
  WIZARD_RETURN_TO_ACTIVATION,
  WIZARD_RETURN_TO_PARAM,
} from '../../routes'
import { buildConfigurePath } from '../../Wizard/WizardStepper'
import ActivationPricingModal from '../ActivationPricingModal'
import ConfigSummaryCards from '../ConfigSummaryCards'
import type {
  ConfigCardKey,
  ConfigSummaryCardItem,
} from '../ConfigSummaryCards'
import { PROMO_CREDIT_CENTS } from '../constants'
import { formatCentsUsd } from '../format'
import LiveUpdatesRequiredModal from '../LiveUpdatesRequiredModal'
import PreparerHandoffModal from '../PreparerHandoffModal'
import { useActivationLink } from '../useActivationLink'
import { useConfigSummary } from '../useConfigSummary'
import { useIsActivationAdmin } from '../useIsActivationAdmin'
import { usePreparerAdmins } from '../usePreparerAdmins'

const t = buildTranslate('recovery_ai.activation.summary')
const tc = buildTranslate('recovery_ai.activation.summary_cards.setting')
const td = buildTranslate('recovery_ai.activation.summary_cards.description')
// AI-1179: the philosophy card's description is now sourced from the SAME
// posture-keyed one-liner the wizard/slider render, so this surface can never
// drift out of sync with the decided copy again — see philosophyDescription
// below.
const tp = buildTranslate('recovery_ai.wizard.philosophy')

const CARD_ORDER: ConfigCardKey[] = [
  'philosophy',
  'brand_voice',
  'active_locations',
  'approved_rewards',
  'escalation',
]

// The three "once activated" benefit bullets, in display order.
const BENEFIT_KEYS = ['responses', 'faster', 'recovery'] as const

// AI-891: a subtle mint wash behind the activation intro (Figma hero node
// 2318-58804). spearmint10 → white on a diagonal, so the tint is strongest
// top-left and clears well before the CTA row — grey70 body copy stays AA on the
// lightest end of the ramp.
const HERO_GRADIENT = `linear-gradient(160deg, ${palette.spearmint10} 0%, ${palette.white} 55%)`
// Both bands share one horizontal gutter so the hero headline and the card column
// left-align; the vertical rhythm differs (hero leads the page, cards close it).
// SectionShell paints each band full-bleed (AppLayout adds no gutter) and caps the
// contents at the shared 1108px column — the AI-886 width fix.
const HERO_PADDING = '40px 24px 32px'
const CARDS_PADDING = '32px 24px 48px'

// Which config-modal row each card's "Change" opens (D6). Typed over the full
// ConfigCardKey union, so adding a card without giving it an editor is a compile
// error rather than a Change button that silently does nothing.
//
// The value side is NOT protected the same way: `RowKey` admits rows this surface
// never passes in `rows`, and the registry resolves an unlisted row to `null` and
// renders nothing — a Change button that opens no dialog and reports no error.
// Expressing that in the type system needs `configRowKeys` to go `as const` plus a
// mapped type (TS 4.7 here, no `satisfies`), which reaches across two other units'
// files; exported instead so the spec can assert it. See M38.
//
// context: this replaces the old per-card route map. Every editor now opens in
// place, so the isActive gate on the Settings / Agent-Tuning routes — which used
// to make three of these cards uneditable in the pre-activation window — no
// longer reaches the activation summary at all.
export const CARD_TO_ROW: Record<ConfigCardKey, RowKey> = {
  philosophy: 'recovery_generosity',
  brand_voice: 'brand_voice',
  active_locations: 'active_locations',
  approved_rewards: 'approved_rewards',
  escalation: 'escalation_contacts',
}

// i18n leaf key for the philosophy posture setting (early-return, not a nested
// ternary). A null posture (config not loaded) falls back to the generic `unset`.
const philosophyKey = (posture: PhilosophyPosture | null): string =>
  posture ? `philosophy.${posture}` : 'philosophy.unset'

// AI-1179: the card's description one-liner, sourced from the single source
// of truth (recovery_ai.wizard.philosophy.<posture>_desc) instead of the old
// static summary_cards.description.philosophy string that rendered the same
// text for every posture. Mirrors philosophyKey's null guard — a config not
// yet loaded falls back to the generic "unset" copy.
const philosophyDescription = (posture: PhilosophyPosture | null): string =>
  posture ? tp(`${posture}_desc`) : td('philosophy_unset')

// i18n leaf key for the brand-voice mode setting.
const brandVoiceKey = (mode: BrandVoiceMode | null): string =>
  mode ? `brand_voice.${mode}` : 'brand_voice.unset'

// i18n leaf key for the escalation routing setting, derived from the two routing
// flags. Early returns keep it out of nested-ternary territory.
const escalationKey = (
  notifyManagers: boolean,
  notifyAdmins: boolean
): string => {
  if (notifyManagers && notifyAdmins) return 'escalation_managers_fallback'
  if (notifyManagers) return 'escalation_managers'
  if (notifyAdmins) return 'escalation_admins'
  return 'escalation_unset'
}

// AI-22 configured-summary page. The single R12 surface every entry context routes
// to. Leads with the activation intro (status pill, "once activated" benefits, the
// activate CTA + sandbox link, and the credit callout), then the five configured
// cards. Forks on billing authority (useIsActivationAdmin): those who may incur
// charges get the "Ready to activate" pricing modal; everyone else gets the
// admin-handoff modal (no activate CTA).
// This component-level gate complements the route-level gate in index.tsx (the
// /activation route admits any RecoveryAI viewer).
const ConfiguredSummary = () => {
  const [css] = useStyletron()
  const dispatch = useDispatch()
  // The gate is the account admin — exact parity with the server's permitted_to?,
  // which short-circuits on the same preset-admin check. See useIsActivationAdmin.
  const gate = useIsActivationAdmin()
  // Editing config and authorizing charges are DIFFERENT authorities, so the cards'
  // Change control stays keyed to manage:MerchantSettings rather than to the
  // activation gate, which additionally requires billing authority. (#3884
  // justified this by the Change destinations being manage-gated routes; D6
  // replaced those routes with in-place modals, so see the ConfigSummaryCards
  // call below for the reason that survives.)
  const canEditConfig = useAbility().can('manage', 'MerchantSettings')
  // Only the non-admin branch renders the handoff modal, so only it needs the list.
  // Fetching unconditionally made every admin issue a GET /merchant_users for a modal
  // they never open.
  const { admins, state: adminsState } = usePreparerAdmins({
    skip: gate.state !== 'not-admin',
  })
  const { activationUrl, copyLink } = useActivationLink()
  const currentUser = useCurrentMerchantUser()
  const merchant = useSelector(selectCurrentMerchant)
  const config = useConfigSummary()
  // AI-1132/AI-1109: the make-good ladder is a SEPARATE source of grantable rewards
  // from the merchant's catalog picks — make-goods live here, not in
  // approved_reward_ids (approvedRewardCount). CONTRACT §3 forbids this query's
  // load/error state from gating the page, so `isSuccess` is read only to fail OPEN
  // (a fail-soft 404/501 or a load-in-flight never blocks activation); a genuinely
  // loaded, empty ladder is what the zero-offerable gate keys on.
  const { data: makeGoodOffers = [], isSuccess: makeGoodOffersLoaded } =
    useGetMakeGoodOffersQuery(undefined, {
      // Only the activate branch consumes the gate, so a preparer (not-admin) never
      // needs the ladder count — skip the GET for them (RTK skip convention).
      skip: gate.state === 'not-admin',
    })
  const [
    notifyActivationAdmin,
    { isLoading: isNotifying, isError: isNotifyError, reset: resetNotify },
  ] = useNotifyActivationAdminMutation()
  const { isDisabled: isLiveUpdatesDisabled } = useLiveUpdatesState()
  const [isPricingModalOpen, setPricingModalOpen] = useState(false)
  const [isLiveUpdatesRequiredModalOpen, setLiveUpdatesRequiredModalOpen] =
    useState(false)
  const [isPreparerModalOpen, setPreparerModalOpen] = useState(false)
  // The one open config-modal row (D6). One value, not five booleans, so two
  // dialogs can never be open at once.
  const [openRow, setOpenRow] = useState<RowKey | null>(null)

  const header = merchant?.name
    ? t('header', { merchant: merchant.name })
    : t('header_fallback')

  const goTo = useCallback((path: string) => dispatch(push(path)), [dispatch])

  // Notify (or re-notify) an eligible admin (AI-923). One shared mutation
  // instance across every row in the modal — see PreparerHandoffModal's
  // isNotifying doc for why that disables every row's control, not just the
  // clicked one. Failure is surfaced inline via notifyError below; swallow the
  // rejection here so it isn't unhandled.
  const handleNotify = useCallback(
    (adminId: string, message: string) => {
      notifyActivationAdmin({ adminMerchantUserId: adminId, message })
        .unwrap()
        .catch(() => {})
    },
    [notifyActivationAdmin]
  )

  // Clear a failed-notify error so a stale banner doesn't carry into the next
  // open — ConfiguredSummary keeps this modal mounted (toggling isOpen).
  const handlePreparerClose = useCallback(() => {
    resetNotify()
    setPreparerModalOpen(false)
  }, [resetNotify])

  // AI-892 "Test your agent in sandbox" destination, forked by edit authority so
  // BOTH personas reach a route they can actually open (the earlier canEditConfig
  // gate hid the link from preparers — a regression on the page built for them):
  //   • editor → the wizard Testing step, tagged returnTo=activation so Cancel
  //     round-trips back here (Wizard doCancel) rather than dead-ending on the
  //     promo page. The step is manage-gated, which is why it's the editor branch.
  //   • preparer → the access-gated Landing agent_tuning tab, whose pre-activation
  //     carve-out (Landing showPreActivationAgentTuning) keeps the live simulator
  //     reachable configured-but-off. A preparer can't enter /configure at all, so
  //     this is their only reachable sandbox.
  const sandboxPath = canEditConfig
    ? `${buildConfigurePath(
        'testing'
      )}?${WIZARD_RETURN_TO_PARAM}=${WIZARD_RETURN_TO_ACTIVATION}`
    : AGENT_TUNING_PATH

  // react-redux-i18n does NOT auto-pluralize; pick _one/_other (or the _unset
  // fallback for a null count) at the call site.
  const pluralSetting = useCallback(
    (count: number | null, base: string): string => {
      if (count === null) return tc(`${base}_unset`)
      return count === 1
        ? tc(`${base}_one`, { count })
        : tc(`${base}_other`, { count })
    },
    []
  )

  const cards: ConfigSummaryCardItem[] = useMemo(() => {
    const titles: Record<ConfigCardKey, string> = {
      philosophy: tc(philosophyKey(config.philosophyPosture)),
      brand_voice: tc(brandVoiceKey(config.brandVoiceMode)),
      active_locations: pluralSetting(
        config.locationsActive,
        'active_locations'
      ),
      approved_rewards: pluralSetting(
        config.approvedRewardCount,
        'approved_rewards'
      ),
      escalation: tc(
        escalationKey(config.notifyLocationManagers, config.notifyAdmins)
      ),
    }
    const descriptions: Record<ConfigCardKey, string> = {
      philosophy: philosophyDescription(config.philosophyPosture),
      brand_voice: td('brand_voice'),
      active_locations: td('active_locations'),
      approved_rewards: td('approved_rewards'),
      escalation: td('escalation'),
    }
    return CARD_ORDER.map(key => ({
      key,
      title: titles[key],
      description: (
        <Text.BodyText4 color="grey70">{descriptions[key]}</Text.BodyText4>
      ),
      onChange: () => setOpenRow(CARD_TO_ROW[key]),
    }))
  }, [config, pluralSetting])

  // AI-1132: the merchant can strip every reward on this page — un-picking all
  // catalog rewards AND declining every make-good ladder rung — which would ship a
  // live agent with nothing to grant during recovery. Offerable = at least one
  // catalog pick OR one non-declined ladder rung (a declined rung is removed
  // server-side, so it is not offerable).
  const activeMakeGoodCount = useMemo(
    () => makeGoodOffers.filter(offer => !offer.declined).length,
    [makeGoodOffers]
  )
  // Known-zero only: approvedRewardCount is null (not 0) until the config loads, and
  // makeGoodOffersLoaded is false until the ladder query succeeds — so this stays
  // false through every loading/outage state and disables the CTA solely on a
  // proven-empty offerable set. Not flag-gated: the bug (AI-1109) exists in both
  // activation-landing flag states.
  const hasNoOfferableRewards =
    config.approvedRewardCount === 0 &&
    makeGoodOffersLoaded &&
    activeMakeGoodCount === 0

  // pillClass is gone: #3885's shared StatusPill renders the ready pill now.
  //
  // Switch on the config query lifecycle.
  // Blank ONLY on a cold first load — a background refetch stays 'refetching', so the
  // page (and an open pricing modal) is never ripped out of the DOM mid-refetch (#1).
  if (config.configState === 'cold') {
    return null
  }

  // A failed config GET must NOT derive isConfigured:false and bounce a fully-configured
  // merchant into the onboarding funnel with no error surface. Render a recoverable retry
  // region instead (mirrors useSimulatorData's "a network error is NOT unconfigured" seam).
  if (config.configState === 'error') {
    return (
      <div
        className={`d-flex flex-column align-items-center ${css({
          gap: '16px',
          padding: '48px 24px',
          textAlign: 'center',
        })}`}
      >
        <Text.BodyText3 color="grey70">{t('load_error')}</Text.BodyText3>
        <Button kind="secondary" onClick={config.refetch}>
          {t('load_error_retry')}
        </Button>
      </div>
    )
  }

  // AI-695 activation entry guard — only a configured, NOT-yet-active merchant may
  // reach the audited activate CTA the pricing modal below fires:
  //   • !isConfigured → the audited PUT only permits operating_mode and never sets
  //     onboarding_completed, so activating a not-yet-onboarded merchant would
  //     half-activate them: a live, billing agent that Landing still treats as
  //     unconfigured (and bounces to GetStarted). Send them back to GetStarted.
  //   • isActive → re-firing the PUT on a live agent does not re-provision
  //     (true→true is not an enable transition) and would show a misleading
  //     "activated" success page for nothing. Send them to the live dashboard.
  // Only reachable by a direct URL (Finalize + Landing's redirect both target the
  // configured-but-off window). Gated on configState === 'settled' so it never fires on
  // stale mid-refetch data. Placed after every hook so the early return never changes
  // the hook call order.
  //
  // context (L10): this can fire with a config modal open, taking the modal with
  // the page. Deliberate. A modal save cannot trigger it directly — no config
  // surface writes onboarding_completed or operating_mode — but the invalidation
  // refetch it settles can surface a mode another admin (or a second tab) changed
  // meanwhile, and then this page is genuinely stale. Do NOT suppress the
  // redirect while a modal is open.
  if (
    config.configState === 'settled' &&
    (!config.isConfigured || config.isActive)
  ) {
    return <Redirect to={RECOVERY_AI_ROOT} />
  }

  return (
    <div className="d-flex flex-column">
      {/* AI-886/AI-891: the activation intro rides a subtle mint-gradient hero band;
          the five configured cards sit below on a distinct grey band. SectionShell
          paints each band full-bleed (AppLayout adds no gutter) with its contents
          capped at the shared 1108px column — the AI-886 width fix over the old
          720px. */}
      <SectionShell background={HERO_GRADIENT} padding={HERO_PADDING}>
        <div className={`d-flex flex-column ${css({ gap: '16px' })}`}>
          <StatusPill tone="ready">{t('ready_pill')}</StatusPill>

          <Text.Header2 tag="div">{header}</Text.Header2>
          <Text.BodyText3 tag="div" color="grey70">
            {t('intro')}
          </Text.BodyText3>

          <div className={`d-flex flex-column ${css({ gap: '8px' })}`}>
            <Text.BodyText4 tag="div" bold color="grey90">
              {t('once_activated')}
            </Text.BodyText4>
            {BENEFIT_KEYS.map(key => (
              <div key={key} className="d-flex align-items-center">
                <i
                  className={`fa fa-check ${css({
                    color: palette.nephritis,
                    marginRight: '8px',
                  })}`}
                  aria-hidden="true"
                />
                <Text.BodyText4 color="grey70">
                  {t(`benefits.${key}`)}
                </Text.BodyText4>
              </div>
            ))}
          </div>

          <div className={`d-flex align-items-center ${css({ gap: '20px' })}`}>
            {gate.state !== 'not-admin' ? (
              // #2 money-safety: the audited activate PUT must never fire on an unsettled
              // config. Disable the CTA unless settled so a background refetch can't leave a
              // clickable button over stale data (the redirect guard above only fires once
              // settled). Also disabled while the permission gate is unresolved: falling
              // through to the preparer branch would flash the wrong CTA, and returning
              // null would jump the layout.
              // AI-1132: and disabled when there is nothing to grant (hasNoOfferableRewards)
              // — see the hint rendered below.
              <Button
                kind="primary"
                disabled={
                  gate.state === 'resolving' ||
                  config.configState !== 'settled' ||
                  hasNoOfferableRewards
                }
                onClick={() =>
                  isLiveUpdatesDisabled
                    ? setLiveUpdatesRequiredModalOpen(true)
                    : setPricingModalOpen(true)
                }
              >
                {t('ready_to_activate')}
                <i
                  className={`fa fa-arrow-right ${css({ marginLeft: '8px' })}`}
                  aria-hidden="true"
                />
              </Button>
            ) : (
              <Button kind="primary" onClick={() => setPreparerModalOpen(true)}>
                {t('notify_admin')}
              </Button>
            )}
            {/* "Test your agent in sandbox" is a borderless text link (kind="minimal"),
                per the Figma. AI-892: routes into the existing wizard Testing step
                (editors) or the Landing agent_tuning tab (preparers) — see
                sandboxPath. Both host the live simulator pre-activation and return to
                this page, unlike a fresh page built to embed it a second way. Rendered
                for both personas: the fork keeps each on a route it can reach, so the
                link is no longer edit-gated (that hid it from the very preparers this
                page serves). */}
            <Button kind="minimal" onClick={() => goTo(sandboxPath)}>
              {t('test_in_sandbox')}
            </Button>
          </div>

          {/* AI-1132: a visible, keyboard-reachable reason the disabled CTA can't fire —
              a bare disabled button gives no feedback, and a disabled control can't host
              a hover tooltip. Only the activate branch renders it (the preparer branch has
              no CTA to explain). */}
          {gate.state !== 'not-admin' && hasNoOfferableRewards && (
            <Text.BodyText4 tag="div" color="cayenne50">
              {t('no_rewards_hint')}
            </Text.BodyText4>
          )}

          <div className="d-flex align-items-center">
            <i
              className={`fa fa-gift ${css({
                color: palette.spearmint,
                marginRight: '8px',
                fontSize: '16px',
              })}`}
              aria-hidden="true"
            />
            <Text.BodyText3 tag="div" color="grey70">
              {/* AI-888: the value-proposition clause is bolded to draw attention;
                  the reassurance tail stays regular weight (credit_suffix). */}
              <Text.BodyText3 bold color="grey90" tag="span">
                {t('credit_emphasis', {
                  amount: formatCentsUsd(PROMO_CREDIT_CENTS),
                })}
              </Text.BodyText3>
              {t('credit_suffix')}
            </Text.BodyText3>
          </div>
        </div>
      </SectionShell>

      <SectionShell backgroundColor={palette.grey05} padding={CARDS_PADDING}>
        <div className={`d-flex flex-column ${css({ gap: '16px' })}`}>
          {/* Above the cards it describes: the registry renders the post-save
              confirmation banner, and the modal that raised it is gone by then.
              hideSaveAndTest is binding, not cosmetic — the registry routes Save-and-
              test through its optional `onTest` callback, and this page has no
              simulator to scroll to, so leaving the button on would ship a control
              that saves, closes, and tests nothing. */}
          <RecoveryConfigModals
            rows={ACTIVATION_ROW_KEYS}
            instanceId="activation-modal"
            openRow={openRow}
            onOpenRow={setOpenRow}
            hideSaveAndTest
            // These dialogs are sized for this page's Figma, not the Settings column
            // the rows default to — see ACTIVATION_ROW_MODAL_WIDTH.
            rowWidths={ACTIVATION_ROW_MODAL_WIDTH}
          />

          {/* Keyed to manage:MerchantSettings (canEditConfig), NOT to the activation gate —
              they answer different questions: "may edit config" vs "may authorize
              charges" (#3884).
              The reason it is load-bearing changed with D6. #3884 argued it from the
              Change destinations being manage-gated ROUTES that eject a non-editor to
              the app root; those routes are gone — every editor now opens in place — so
              the trigger itself is the gate. Three of the five editors (generosity,
              brand voice, approved rewards) carry no CASL check of their own, so opening
              one for a preparer would hand them a form whose inputs all look live.
              Save is separately gated on manage/MerchantSettings — in ConfigRowModal for four
              of the five rows, and on BrandVoiceModal's own footer for brand voice,
              which short-circuits that shell — so this trigger is defence in depth
              rather than the only gate. Keep BOTH: the shell term is per-row, so a new
              row that owns its footer would arrive uncovered again.
              #3884's FLAG(open-question AI-22) — whether preparers should be able to
              deep-link into the access-viewable Settings panes — is moot here: there are
              no deep links left to follow. */}
          <ConfigSummaryCards cards={cards} showChange={canEditConfig} />
        </div>
      </SectionShell>

      {gate.state === 'resolving' ? null : gate.state === 'admin' ? (
        <ActivationPricingModal
          isOpen={isPricingModalOpen}
          onClose={() => setPricingModalOpen(false)}
          onLiveUpdatesRequired={() => {
            setPricingModalOpen(false)
            setLiveUpdatesRequiredModalOpen(true)
            // The server refused, so the merchant record is stale (another
            // admin turned live updates off). Refetch it so this page and the
            // Live updates page read the real state.
            if (merchant?.id) dispatch(getMerchant(merchant.id))
          }}
        />
      ) : (
        <PreparerHandoffModal
          isOpen={isPreparerModalOpen}
          onClose={handlePreparerClose}
          admins={admins}
          adminsState={adminsState}
          merchantName={merchant?.name ?? null}
          preparerFirstName={currentUser?.first_name ?? null}
          activationUrl={activationUrl}
          onCopyLink={copyLink}
          onNotify={handleNotify}
          isNotifying={isNotifying}
          notifyError={isNotifyError}
        />
      )}

      <LiveUpdatesRequiredModal
        isOpen={isLiveUpdatesRequiredModalOpen}
        onClose={() => setLiveUpdatesRequiredModalOpen(false)}
      />
    </div>
  )
}

export default ConfiguredSummary
